// 회수완료 모달 — 이상 없음 / 파손(차감액·메모) + 세이펜 반납 확인(세이펜 대여 주문만)
//                + 반납 권수 확인(전집) + 반환 지연 차감(회수 예정일보다 늦으면 기본값 제시)
//
// ⚠️ 배경 클릭 닫기는 useBackdropClose 훅만 쓴다(CLAUDE.md). 오버레이에 onClick을 직접
//    물리면 모달 안에서 드래그하다 밖에서 손을 떼는 순간 입력값이 날아간다.
// ⚠️ 차감액 상한·메모 필수 검증은 서버(services/status.py)가 최종 판정한다. 여기 검증은
//    사용자에게 바로 알려주기 위한 것이고, 규칙의 출처는 서버다.
import React, { useMemo, useState } from "react";

import useBackdropClose from "../../hooks/useBackdropClose";
import { MONTHLY, formatWon, refundGuide } from "./format";
import "./rental.css";

const CONDITION_OK = "OK";
const CONDITION_DAMAGED = "DAMAGED";
// 세이펜 반납 — 서버 models.SaypenReturn과 같은 값.
const SAYPEN_OK = "OK";
const SAYPEN_MISSING = "MISSING";
const SAYPEN_DAMAGED = "DAMAGED";

export default function PickupModal({ order, busy, onClose, onConfirm }) {
  const backdrop = useBackdropClose(onClose);
  const [condition, setCondition] = useState(CONDITION_OK);
  const [deduction, setDeduction] = useState("");
  const [memo, setMemo] = useState("");
  const [localError, setLocalError] = useState("");
  // "환불도 지금 처리함" — 회수 정산값으로 보증금·배송비 환불을 오늘 날짜로 기록(서버 record_refund).
  // 실제 환불(카드 부분취소·계좌이체)을 이 자리에서 끝냈을 때만 켠다. 기본은 꺼짐.
  const [refundNow, setRefundNow] = useState(false);
  // 세이펜 — 체크하면 반납 확인, 해제 상태면 미반납/파손 중 하나와 차감액을 받는다.
  const saypenCheck = Boolean(order.saypen_check);
  const [saypenReturned, setSaypenReturned] = useState(false);
  const [saypenProblem, setSaypenProblem] = useState(SAYPEN_MISSING);
  const [saypenDeduction, setSaypenDeduction] = useState("");
  const saypenIssue = saypenCheck && !saypenReturned;
  // 전집 반납 권수 — 체크하면 전권 반납, 해제 상태면 부족 권수·차감액을 받는다.
  const volumeCount = order.rental_kind === MONTHLY ? Number(order.volume_count || 0) : 0;
  const volumeCheck = volumeCount > 0;
  const [volumesReturned, setVolumesReturned] = useState(false);
  const [missingVolumes, setMissingVolumes] = useState("");
  const [volumeDeduction, setVolumeDeduction] = useState("");
  const volumeIssue = volumeCheck && !volumesReturned;
  // 반환 지연 — 지연 일수 × 1일당 요금을 기본값으로 채워 두고 담당자가 고칠 수 있다.
  // 서버는 보낸 값만 차감한다(services/status._validate_pickup).
  const lateDays = Number(order.late_days_now || 0);
  const lateFee = Number(order.late_fee_per_day || 0);
  const [lateDeduction, setLateDeduction] = useState(
    lateDays > 0 ? String(Math.round(Number(order.late_deduction_suggested || 0))) : ""
  );

  // 환불 기준액 = 보증금 + 배송·회수비(직접 수령·반납만 — 서버 return_refund_fee).
  // 서버(services/status._validate_pickup)도 같은 기준으로 검증한다.
  const depositOnly = Number(order.deposit ?? 20000);
  const returnFee = Number(order.return_refund_fee || 0);
  const deposit = depositOnly + returnFee;
  // 항목별 차감액(숫자) — 화면 합계와 제출 값이 같은 계산을 쓴다.
  const amounts = useMemo(
    () => ({
      item: condition === CONDITION_DAMAGED ? Number(deduction || 0) : 0,
      saypen: saypenIssue ? Number(saypenDeduction || 0) : 0,
      volume: volumeIssue ? Number(volumeDeduction || 0) : 0,
      late: lateDays > 0 ? Number(lateDeduction || 0) : 0,
    }),
    [condition, deduction, saypenIssue, saypenDeduction, volumeIssue, volumeDeduction,
     lateDays, lateDeduction]
  );
  const totalDeduction = Object.values(amounts).reduce(
    (sum, v) => sum + (Number.isNaN(v) ? 0 : v), 0
  );
  const refund = Math.max(0, deposit - totalDeduction);

  // 파손·세이펜·권수 부족 차감은 돈이 걸린 판단이라 근거 메모가 필수다(지연은 계산값이라 아님).
  const memoRequired = condition === CONDITION_DAMAGED || saypenIssue || volumeIssue;

  const submit = () => {
    setLocalError("");
    const { item, saypen, volume, late } = amounts;
    if (Number.isNaN(item) || item < 0) {
      setLocalError("차감액은 0원 이상이어야 합니다.");
      return;
    }
    if (saypenIssue && (saypenDeduction === "" || Number.isNaN(saypen) || saypen < 0)) {
      setLocalError("세이펜 미반납·파손은 차감액을 입력해야 합니다 (0원 이상).");
      return;
    }
    if (volumeIssue) {
      const missing = Number(missingVolumes);
      if (!Number.isInteger(missing) || missing < 1 || missing > volumeCount) {
        setLocalError(`부족 권수는 1권 이상 ${volumeCount}권 이하로 입력해야 합니다.`);
        return;
      }
      if (volumeDeduction === "" || Number.isNaN(volume) || volume < 0) {
        setLocalError("권수 부족은 차감액을 입력해야 합니다 (0원 이상).");
        return;
      }
    }
    if (Number.isNaN(late) || late < 0) {
      setLocalError("반환 지연 차감액은 0원 이상이어야 합니다.");
      return;
    }
    if (totalDeduction > deposit) {
      setLocalError(`차감액 합계는 ${formatWon(deposit)} 이하여야 합니다.`);
      return;
    }
    if (memoRequired && !memo.trim()) {
      setLocalError("파손·세이펜·권수 부족 차감은 근거 메모가 필수입니다.");
      return;
    }
    const payload = { condition, deduction_amount: item, note: memo };
    if (saypenCheck) {
      payload.saypen_return = saypenReturned ? SAYPEN_OK : saypenProblem;
      payload.saypen_deduction = saypen;
    }
    if (volumeCheck) {
      payload.volumes_returned = volumesReturned;
      payload.missing_volumes = volumesReturned ? 0 : Number(missingVolumes);
      payload.volume_deduction = volume;
    }
    if (lateDays > 0) {
      payload.late_deduction = late;
    }
    if (refundNow) payload.record_refund = true;
    onConfirm(payload);
  };

  return (
    <div className="rental-overlay" {...backdrop}>
      <div className="rental-modal">
        <h3 className="rental-section-title">
          회수완료 — {order.receiver_name || order.order_id}
        </h3>

        {/* 회수 확인 수량 — 쌍둥이 추가분을 합친 값(서버 options.kit_summary) */}
        {(order.kit_lines || []).length > 0 && (
          <div className="rental-row">
            <span className="rental-row-label">회수 확인</span>
            <span className="rental-row-value">{order.kit_lines.join(" · ")}</span>
          </div>
        )}

        <div className="rental-choice">
          <button
            type="button"
            className={`rental-btn-choice${
              condition === CONDITION_OK ? " rental-btn-choice--active" : ""
            }`}
            onClick={() => setCondition(CONDITION_OK)}
          >
            이상 없음
          </button>
          <button
            type="button"
            className={`rental-btn-choice${
              condition === CONDITION_DAMAGED ? " rental-btn-choice--active" : ""
            }`}
            onClick={() => setCondition(CONDITION_DAMAGED)}
          >
            파손
          </button>
        </div>

        {condition === CONDITION_DAMAGED && (
          <div className="rental-row">
            <span className="rental-row-label">차감액</span>
            <input
              className="rental-input"
              type="number"
              min="0"
              max={deposit}
              value={deduction}
              onChange={(e) => setDeduction(e.target.value)}
              placeholder={`0 ~ ${deposit}`}
            />
          </div>
        )}

        {/* 세이펜 — 대여한 주문만(서버 saypen_check). 반납 확인 또는 미반납·파손 입력이 있어야 확정된다. */}
        {saypenCheck && (
          <div className="rental-saypen">
            <label className="rental-check">
              <input
                type="checkbox"
                checked={saypenReturned}
                onChange={(e) => setSaypenReturned(e.target.checked)}
              />
              <span>세이펜 본체·충전 케이블 반납 확인</span>
            </label>
            {saypenIssue && (
              <>
                <div className="rental-choice">
                  <button
                    type="button"
                    className={`rental-btn-choice${
                      saypenProblem === SAYPEN_MISSING ? " rental-btn-choice--active" : ""
                    }`}
                    onClick={() => setSaypenProblem(SAYPEN_MISSING)}
                  >
                    미반납
                  </button>
                  <button
                    type="button"
                    className={`rental-btn-choice${
                      saypenProblem === SAYPEN_DAMAGED ? " rental-btn-choice--active" : ""
                    }`}
                    onClick={() => setSaypenProblem(SAYPEN_DAMAGED)}
                  >
                    파손
                  </button>
                </div>
                <div className="rental-row">
                  <span className="rental-row-label">세이펜 차감액</span>
                  <input
                    className="rental-input"
                    type="number"
                    min="0"
                    max={deposit}
                    value={saypenDeduction}
                    onChange={(e) => setSaypenDeduction(e.target.value)}
                    placeholder={`0 ~ ${deposit}`}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* 전집 반납 권수 — 서버 volume_count(상품 설정의 본책 권수). 확인 없이는 확정되지 않는다. */}
        {volumeCheck && (
          <div className="rental-saypen">
            <label className="rental-check">
              <input
                type="checkbox"
                checked={volumesReturned}
                onChange={(e) => setVolumesReturned(e.target.checked)}
              />
              <span>반납 권수 확인 (본책 {volumeCount}권)</span>
            </label>
            {volumeIssue && (
              <>
                <div className="rental-row">
                  <span className="rental-row-label">부족 권수</span>
                  <input
                    className="rental-input rental-input--short"
                    type="number"
                    min="1"
                    max={volumeCount}
                    value={missingVolumes}
                    onChange={(e) => setMissingVolumes(e.target.value)}
                    placeholder={`1 ~ ${volumeCount}`}
                  />
                  <span className="rental-row-unit">권</span>
                </div>
                <div className="rental-row">
                  <span className="rental-row-label">권수 차감액</span>
                  <input
                    className="rental-input"
                    type="number"
                    min="0"
                    max={deposit}
                    value={volumeDeduction}
                    onChange={(e) => setVolumeDeduction(e.target.value)}
                    placeholder={`0 ~ ${deposit}`}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* 반환 지연 — 회수 예정일보다 늦을 때만. 기본값 = 지연 일수 × 1일당 요금(서버 계산). */}
        {lateDays > 0 && (
          <div className="rental-saypen">
            <div className="rental-row">
              <span className="rental-row-label">반환 지연</span>
              <span className="rental-row-value">
                {lateDays}일 × {formatWon(lateFee)} = {formatWon(lateDays * lateFee)}
              </span>
            </div>
            <div className="rental-row">
              <span className="rental-row-label">지연 차감액</span>
              <input
                className="rental-input"
                type="number"
                min="0"
                max={deposit}
                value={lateDeduction}
                onChange={(e) => setLateDeduction(e.target.value)}
              />
            </div>
          </div>
        )}

        {memoRequired && (
          <div className="rental-row">
            <span className="rental-row-label">근거 메모</span>
          </div>
        )}
        <textarea
          className="rental-memo"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder={
            memoRequired ? "파손 부위·정도, 세이펜·부족 권수 내용을 적습니다 (필수)" : "메모 (선택)"
          }
        />

        {totalDeduction > 0 && (
          <div className="rental-row">
            <span className="rental-row-label">차감 합계</span>
            <span className="rental-row-value">{formatWon(totalDeduction)}</span>
          </div>
        )}
        <div className="rental-row">
          <span className="rental-row-label">환불액</span>
          <span className="rental-row-value">
            {formatWon(refund)} (보증금 {formatWon(depositOnly)}
            {returnFee > 0 ? ` + 배송·회수비 ${formatWon(returnFee)}` : ""})
          </span>
        </div>

        <div className="rental-warn-box">
          {/* 결제수단별 처리 방법 — 카드/무통장이 다르다(format.refundGuide) */}
          {refund > 0 && <strong className="rental-refund-guide">{refundGuide(order, refund)}</strong>}
          카페24에 반품·환불 API가 없어 환불은 카페24 관리자에서 수동으로 처리해야 합니다.
          확정하면 목록에 "수동처리 필요"로 표시됩니다.
        </div>

        {refund > 0 && (
          <label className="rental-check" style={{ margin: "8px 0" }}>
            <input type="checkbox" checked={refundNow} onChange={(e) => setRefundNow(e.target.checked)} />
            환불도 지금 처리함 (오늘 날짜로 환불 기록 저장)
          </label>
        )}

        {localError && <div className="rental-error">{localError}</div>}

        <div className="rental-modal-actions">
          <button type="button" className="rental-btn-cancel" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="rental-btn-confirm"
            onClick={submit}
            disabled={busy}
          >
            {busy ? "처리 중…" : "확정"}
          </button>
        </div>
      </div>
    </div>
  );
}
